import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@material-ui/core';
import { useNotify } from 'react-admin';
import axios from 'utils/axios';

const MilestoneDeliveryDialog = ({ milestone, open, onClose, onSuccess }) => {
  const [count, setCount] = useState('');
  const [loading, setLoading] = useState(false);
  const notify = useNotify();

  const handleSubmit = async e => {
    e.preventDefault();
    e.stopPropagation();

    const countValue = parseInt(count, 10);

    // Validation
    if (!countValue || countValue <= 0) {
      notify('Count must be greater than 0', { type: 'error' });
      return;
    }

    if (countValue > milestone.guaranteed_gains_max) {
      notify(`Count cannot exceed ${milestone.guaranteed_gains_max}`, { type: 'error' });
      return;
    }

    setLoading(true);

    try {
      await axios.post(`/v1/milestones/${milestone.id}/deliver_with_subs`, {
        count: countValue,
      });

      notify('Milestone delivered successfully', { type: 'success' });
      onSuccess();
      onClose();
      setCount('');
    } catch (error) {
      notify('Error delivering milestone', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCount('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Deliver Guaranteed Gains Milestone</DialogTitle>

      <DialogContent>
        <Typography variant="body2" style={{ marginBottom: 16 }}>
          Milestone ID: {milestone?.id}
        </Typography>

        <Typography variant="body2" style={{ marginBottom: 16 }}>
          Current GG: {milestone?.guaranteed_gains_current || 0} / Max GG:{' '}
          {milestone?.guaranteed_gains_max || 0}
        </Typography>

        <TextField
          autoFocus
          label="Count"
          type="number"
          fullWidth
          variant="outlined"
          value={count}
          onChange={e => setCount(e.target.value)}
          onKeyPress={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmit();
            }
          }}
          inputProps={{
            min: 1,
            max: milestone?.guaranteed_gains_max || 1,
          }}
          helperText={`Enter a count between 1 and ${milestone?.guaranteed_gains_max || 1}`}
          required
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
          onMouseDown={e => e.stopPropagation()}
          onMouseUp={e => e.stopPropagation()}
        >
          {loading ? 'Delivering...' : 'Deliver'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const MilestoneDeliveryButton = ({ record, onSuccess }) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  // Only show button for guaranteed gains milestones in pending status
  if (!record?.guaranteed_gains || record?.status !== 'pending') {
    return null;
  }

  return (
    <>
      <Button
        size="small"
        color="primary"
        variant="outlined"
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
          setDialogOpen(true);
        }}
        style={{ marginLeft: 8 }}
      >
        Deliver
      </Button>

      <MilestoneDeliveryDialog
        milestone={record}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={onSuccess}
      />
    </>
  );
};

export const MilestoneApproveButton = ({ record, onSuccess }) => {
  const notify = useNotify();
  const [loading, setLoading] = useState(false);

  // Only show button for milestones awaiting approval
  if (record?.status !== 'awaiting_approval') {
    return null;
  }

  const handleApprove = async e => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to approve this milestone?')) {
      return;
    }

    setLoading(true);

    try {
      await axios.post(`/v1/milestones/${record.id}/approve`);
      notify('Milestone approved successfully', { type: 'success' });
      onSuccess();
    } catch (error) {
      notify('Error approving milestone', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      size="small"
      color="primary"
      variant="contained"
      onClick={handleApprove}
      disabled={loading}
      style={{ marginLeft: 8 }}
    >
      {loading ? 'Approving...' : 'Approve'}
    </Button>
  );
};

export const MilestoneRejectButton = ({ record, onSuccess }) => {
  const notify = useNotify();
  const [loading, setLoading] = useState(false);

  // Only show button for milestones awaiting approval
  if (record?.status !== 'awaiting_approval') {
    return null;
  }

  const handleReject = async e => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to reject this milestone?')) {
      return;
    }

    setLoading(true);

    try {
      await axios.post(`/v1/milestones/${record.id}/reject`, {
        rejection_reason: 'Rejected by admin',
      });
      notify('Milestone rejected successfully', { type: 'success' });
      onSuccess();
    } catch (error) {
      notify('Error rejecting milestone', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      size="small"
      color="secondary"
      variant="outlined"
      onClick={handleReject}
      disabled={loading}
      style={{ marginLeft: 8 }}
    >
      {loading ? 'Rejecting...' : 'Reject'}
    </Button>
  );
};

export default MilestoneDeliveryButton;
